import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { HelpCircle, Check, EyeOff, Send, Loader2 } from "lucide-react";
import api, { fileUrl } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Loading, EmptyState } from "@/components/common/Loading";
import { AuthorLink } from "@/components/social/AuthorLink";
import { timeAgo } from "@/lib/timeago";

const statusBadge = (s) => {
  if (s === "answered") return <Badge className="bg-teal-50 text-teal-700">Answered</Badge>;
  if (s === "submitted") return <Badge variant="secondary">Submitted</Badge>;
  if (s === "ignored") return <Badge variant="secondary">Ignored</Badge>;
  return <Badge variant="secondary">Pending</Badge>;
};

function AnswerDialog({ q, onDone }) {
  const [open, setOpen] = useState(false);
  const [answer, setAnswer] = useState(q.answer || "");
  const [publish, setPublish] = useState(!!q.published);
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!answer.trim()) return;
    setBusy(true);
    try { await api.post(`/questions/${q.id}/answer`, { answer: answer.trim(), publish }); toast.success("Answer sent"); setOpen(false); onDone(); }
    catch (e) { toast.error("Could not send"); } finally { setBusy(false); }
  };
  return (
    <>
      <Button size="sm" onClick={() => setOpen(true)} data-testid={`answer-open-${q.id}`}><Send className="mr-1.5 h-4 w-4" /> {q.status === "answered" ? "Edit answer" : "Answer"}</Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Answer question</DialogTitle><DialogDescription>{q.body}</DialogDescription></DialogHeader>
          <Textarea value={answer} onChange={(e) => setAnswer(e.target.value.slice(0, 700))} maxLength={700} rows={5} placeholder="Write your answer…" data-testid={`answer-input-${q.id}`} />
          <div className="text-right text-xs tabular-nums text-muted-foreground" data-testid={`answer-count-${q.id}`}>{answer.length}/700</div>
          <div className="flex items-center gap-2">
            <Switch id={`pub-${q.id}`} checked={publish} onCheckedChange={setPublish} data-testid={`answer-publish-${q.id}`} />
            <Label htmlFor={`pub-${q.id}`} className="cursor-pointer">Publish this Q&amp;A on my public profile</Label>
          </div>
          <DialogFooter>
            <Button variant="secondary" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={submit} disabled={busy || !answer.trim()} data-testid={`answer-submit-${q.id}`}>{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Send answer"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function Asker({ a }) {
  return (
    <div className="flex items-center gap-2.5">
      <Avatar className="h-9 w-9">
        {a && a.headshot_url && <AvatarImage src={fileUrl(a.headshot_url)} alt="" />}
        <AvatarFallback className="bg-secondary text-xs">{(a && a.display_name ? a.display_name[0] : "?")}</AvatarFallback>
      </Avatar>
      <span className="text-sm font-[560] text-foreground"><AuthorLink card={a} className="text-sm font-[560] text-foreground" fallback="Member" testId="question-asker-link" /></span>
    </div>
  );
}

export default function Questions() {
  const [inbox, setInbox] = useState(null);
  const [sent, setSent] = useState(null);

  const load = async () => {
    try {
      const [i, s] = await Promise.all([api.get("/questions/inbox"), api.get("/questions/sent")]);
      setInbox(i.data); setSent(s.data);
    } catch (e) { setInbox([]); setSent([]); }
  };
  useEffect(() => { load(); }, []);

  const ignore = async (id) => {
    try { await api.post(`/questions/${id}/ignore`); toast.success("Question ignored"); load(); }
    catch (e) { toast.error("Action failed"); }
  };

  if (!inbox || !sent) return <Loading />;
  const pendingCount = inbox.filter((q) => q.status === "pending").length;

  return (
    <div>
      <h1 className="text-2xl font-[680] tracking-[-0.01em] text-foreground">Questions</h1>
      <p className="mt-1 text-muted-foreground">Answer questions on your profiles and track the ones you've asked.</p>

      <Tabs defaultValue="inbox" className="mt-6">
        <TabsList>
          <TabsTrigger value="inbox" data-testid="tab-q-inbox">Received{pendingCount > 0 && <Badge className="ml-2 bg-primary text-primary-foreground">{pendingCount}</Badge>}</TabsTrigger>
          <TabsTrigger value="sent" data-testid="tab-q-sent">Asked ({sent.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="inbox" className="mt-4">
          {inbox.length === 0 ? (
            <EmptyState icon={HelpCircle} title="No questions yet" description="When members ask questions on your profiles, they'll appear here." testId="q-inbox-empty" />
          ) : (
            <div className="space-y-3">
              {inbox.map((q) => (
                <Card key={q.id} className="p-5" data-testid={`q-inbox-${q.id}`}>
                  <div className="flex items-start justify-between gap-3">
                    <Asker a={q.asker} />
                    <div className="flex items-center gap-2">{statusBadge(q.status)}{q.published && <Badge variant="secondary">Published</Badge>}</div>
                  </div>
                  <p className="mt-3 font-[560] text-foreground">{q.body}</p>
                  {q.answer && <div className="mt-2 rounded-lg bg-secondary/60 px-3 py-2 text-sm text-foreground">{q.answer}</div>}
                  <div className="mt-3 flex items-center gap-2">
                    <AnswerDialog q={q} onDone={load} />
                    {q.status !== "ignored" && q.status !== "answered" && <Button size="sm" variant="secondary" onClick={() => ignore(q.id)} data-testid={`ignore-${q.id}`}><EyeOff className="mr-1.5 h-4 w-4" /> Ignore</Button>}
                    <span className="ml-auto text-xs text-muted-foreground">{timeAgo(q.created_at)}</span>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="sent" className="mt-4">
          {sent.length === 0 ? (
            <EmptyState icon={HelpCircle} title="You haven't asked anything yet" description="Ask a question from any profile to start a conversation." testId="q-sent-empty" />
          ) : (
            <div className="space-y-3">
              {sent.map((q) => (
                <Card key={q.id} className="p-5" data-testid={`q-sent-${q.id}`}>
                  <div className="flex items-center justify-between"><p className="font-[560] text-foreground">{q.body}</p>{statusBadge(q.status)}</div>
                  {q.answer ? (
                    <div className="mt-2 rounded-lg border-l-2 border-l-primary bg-secondary/50 px-3 py-2 text-sm text-foreground">{q.answer}</div>
                  ) : <p className="mt-2 text-sm text-muted-foreground">Awaiting a response.</p>}
                  <p className="mt-2 text-xs text-muted-foreground">{timeAgo(q.created_at)}</p>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
